import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, Typography } from 'antd';
import React, { useMemo } from 'react';
import { IBestDataset } from '../../../../../types/analytics';
import { IMarker } from '../../../../../types/marker';
import { ReportsUserInput } from '../../../../../types/reports';
import { AnalyticsSection } from '../../../Analytics/components/Layout/AnalyticsSection';
import {
  REQUEST_REPORT_STATUS,
  useOndemandParams,
} from '../../hooks/useOndemandParams';
import styles from './Metocean.module.scss';
import { IReportCard } from './ReportCard';
import ReportsInputForm from './ReportsInputForm';
import { ReportsList } from './ReportsList';

const { Text } = Typography;

interface ReportsProps {
  datasets: IBestDataset[];
  userInput: ReportsUserInput;
  updateUserInput: React.Dispatch<Partial<ReportsUserInput>>;
  point?: IMarker;
  defaultDataset?: any;
}

// TODO: Remove after feature completed
// const STATIC_REPORTS = [
//   {
//     id: '1',
//     title: 'Bass Strait, AU',
//     thumbnailUrl: 'static/media/SampleThumbnail.pdf',
//     pdfUrl: 'static/media/Sample.pdf',
//     excelUrl: 'static/media/Sample.xlsx',
//   },
// ];

export default function ReportsOnDemand(props: ReportsProps) {
  const { datasets, userInput, updateUserInput, defaultDataset, point } = props;

  const {
    datasetSW,
    datasetHD,
    datasetWind,
    timeStep,
    startDate,
    endDate,
    requestReportStatus,
    doStartDateChange,
    doEndDateChange,
    disabledStartDate,
    disabledEndDate,
    handleDatasetChange,
    handleUpdate,
  } = useOndemandParams({
    datasets,
    userInput,
    updateUserInput,
    defaultDataset,
    pointName: point?.label!,
    location: point?.coordinates!,
  });

  const isReportError = requestReportStatus === REQUEST_REPORT_STATUS.ERROR;
  const isReportSuccess = requestReportStatus === REQUEST_REPORT_STATUS.SUCCESS;
  const isReportPending = requestReportStatus === REQUEST_REPORT_STATUS.PENDING;

  const handleExcelDownload = (report: IReportCard): void => {
    window.open(report.excelUrl, '_blank');
  };

  const handlePdfDownload = (report: IReportCard): void => {
    window.open(report.pdfUrl, '_blank');
  };

  const plotInputContent = useMemo(
    () => (
      <div className={styles.plotInputContainer}>
        <ReportsInputForm
          datasets={datasets}
          datasetSW={datasetSW}
          datasetHD={datasetHD}
          datasetWind={datasetWind}
          timeStep={timeStep}
          onSelectDataset={handleDatasetChange}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={doStartDateChange}
          onEndDateChange={doEndDateChange}
          disabledStartDate={disabledStartDate}
          disabledEndDate={disabledEndDate}
          isOfflineDataset={false}
        />
      </div>
    ),
    [
      datasets,
      datasetSW,
      datasetHD,
      datasetWind,
      timeStep,
      handleDatasetChange,
      startDate,
      endDate,
      doStartDateChange,
      doEndDateChange,
      disabledStartDate,
      disabledEndDate,
    ]
  );

  const successInfo = useMemo(
    () => (
      <div className={styles.reportSuccess}>
        <CheckCircleOutlined style={{ fontSize: '24px' }} />
        <span>Your request has been successfully submitted!</span>
        <span>Report will be sent with an e-mail within two hours.</span>
      </div>
    ),
    []
  );

  const errorInfo = useMemo(
    () => (
      <div className={styles.reportError}>
        Something went wrong when processing your request
      </div>
    ),
    []
  );

  const updateContent = useMemo(
    () => (
      <div className={styles.requestReportContainer}>
        {!isReportSuccess && (
          <>
            <Button
              type='primary'
              id='Reports-Update_Button'
              onClick={handleUpdate}
              style={{
                width: '130px',
                height: '36px',
              }}
              loading={isReportPending}
              // TODO: ENABLE WHEN FEATURE READY
              disabled
            >
              {!isReportPending && <span>Request Report</span>}
            </Button>
            {isReportError && errorInfo}
          </>
        )}
        {isReportSuccess && successInfo}
      </div>
    ),
    [
      handleUpdate,
      isReportError,
      isReportSuccess,
      isReportPending,
      successInfo,
      errorInfo,
    ]
  );

  const reportsContent = useMemo(() => {
    // const reports = STATIC_REPORTS;
    return (
      <ReportsList
        reports={[]}
        onPdfDownload={handlePdfDownload}
        onExcelDownload={handleExcelDownload}
      />
    );
  }, []);

  const additionalContent = useMemo(
    () => (
      <div className={styles.additionalInfoContainer}>
        <Text type='secondary'>
          <Text strong>Delivery</Text>:{' '}
          <i>
            The requested report (PDF and Excel) will be provided by email
            within 1 hour
          </i>
        </Text>
      </div>
    ),
    []
  );

  return (
    <>
      <AnalyticsSection
        plotInputContent={plotInputContent}
        updateContent={updateContent}
        chartContent={reportsContent}
        isLoading={false}
        additionalContent={additionalContent}
      />

      {requestReportStatus}
    </>
  );
}
